import {
    Component,
    lazy,
    Suspense,
    useEffect,
    useRef,
    useSyncExternalStore,
} from "react";
import { connect } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { queryChildren, querySetting } from "@/apis";
import ErrorBoundary from "@/components/ErrorBoundary";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import List from "@/components/List";
import SearchSheet from "@/components/SearchSheet";
import VideoPlayer from "@/components/VideoPlayer";
import { mapStateToProps } from "@/store";
import { cdnUrl } from "@/utils";
import { bootstrapSession, getAuthSnapshot, subscribeAuth } from "@/utils/auth";
import "@/static/iconfont.2.5";
import "./styles/index.scss";

const About = lazy(() => import("@/pages/about"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const Daily = lazy(() => import("@/pages/daily"));
const Login = lazy(() => import("@/pages/login"));
const Notfound = lazy(() => import("@/pages/notfound"));

export const RequireAuth = ({ children }) => {
    const location = useLocation();
    const auth = useSyncExternalStore(subscribeAuth, getAuthSnapshot);

    useEffect(() => {
        void bootstrapSession();
    }, []);

    if (auth.status === "unknown") {
        return <div className="loading" aria-label="正在恢复登录状态" />;
    }

    if (auth.status !== "authenticated") {
        return (
            <Navigate
                to="/login"
                replace
                state={{ query: { ref: location.pathname } }}
            />
        );
    }

    return children;
};

class App extends Component {
    state = {
        fixed: "",
        nopeAuth: true,
        headobj: undefined,
        aboutinfo: undefined,
        sword: undefined,
        searchOpen: false, // 首页搜索浮层开关（icon 在 banner 右上角）
    };

    async componentDidMount() {
        const { dispatch } = this.props;
        try {
            const [ret, childrenRet] = await Promise.all([
                querySetting(),
                queryChildren().catch((error) => {
                    console.error("读取孩子档案失败", error);
                    return { code: -1, data: [] };
                }),
            ]);
            if (!ret.data || ret.code !== 200) {
                console.error(ret.msg || "请求接口异常");
                return;
            }
            // banner 背景图按视口宽度出多档分辨率（七牛 imageMogr2 实时缩放），
            // 配合 <img srcSet> 让手机拿小图、PC/高分屏拿大图，避免 375px 小图被拉糊。
            const bgBase = cdnUrl(ret.data.bgimg);
            const bgimgSrc = (width, quality) =>
                `${bgBase}?imageMogr2/auto-orient/thumbnail/${width}/strip/quality/${quality}`;
            this.setState({
                headobj: {
                    bgimg: bgBase ? bgimgSrc(750, 60) : "",
                    bgimgSrcSet: bgBase
                        ? [
                              `${bgimgSrc(375, 55)} 375w`,
                              `${bgimgSrc(750, 60)} 750w`,
                              `${bgimgSrc(1500, 65)} 1500w`,
                              `${bgimgSrc(2250, 65)} 2250w`,
                          ].join(", ")
                        : "",
                    avatar: ret.data.avatar,
                    title: ret.data.sitename,
                    saynum: ret.data.saynum,
                    talks: ret.data.talks,
                },
                sword: ret.data.sword,
                aboutinfo: ret.data.richtext,
            });
            const children =
                childrenRet.code === 200 && Array.isArray(childrenRet.data)
                    ? Object.fromEntries(
                          childrenRet.data.map((child) => [child.no, child]),
                      )
                    : {};
            dispatch({
                type: "UPDATE_SYSTEM",
                system: { ...ret.data, category: children },
            });
        } catch (error) {
            console.error("读取站点设置失败", error);
        }
    }

    setHeadFix = (fixed = false, className = "") => {
        this.setState({ fixed: fixed ? `fixed ${className}` : className });
    };

    toggleSearch = () => {
        this.setState((state) => ({ searchOpen: !state.searchOpen }));
    };

    closeSearch = () => {
        this.setState({ searchOpen: false });
    };

    getMainHeight = () => this.main?.offsetHeight ?? 0;

    setAuthenticatedLayout = (isUnauthenticated = true) => {
        this.setState({ nopeAuth: isUnauthenticated });
    };

    render() {
        const { aboutinfo, fixed, headobj, nopeAuth, sword, searchOpen } =
            this.state;
        const { pathname } = this.props.location;
        const showHeader = !pathname.startsWith("/item/");
        // 搜索 icon 只在首页显示（浮层也只在首页挂载，离开页面自动收起）
        const homeSearchToggle = pathname === "/" ? this.toggleSearch : null;

        return (
            <section
                className={`${showHeader ? fixed : ""} ${nopeAuth ? "nope-auth" : ""}`.trim()}
                ref={(main) => {
                    this.main = main;
                }}
            >
                {showHeader && (
                    <Header
                        headobj={headobj}
                        searchOpen={searchOpen}
                        onSearchToggle={homeSearchToggle}
                    />
                )}
                <ErrorBoundary resetKey={pathname}>
                    <Suspense
                        fallback={
                            <div className="loading" aria-label="加载中" />
                        }
                    >
                        <Routes>
                            <Route
                                path="/"
                                element={
                                    <List
                                        setHeadFix={this.setHeadFix}
                                        getMainHeight={this.getMainHeight}
                                    />
                                }
                            />
                            <Route
                                path="/about"
                                element={
                                    <About
                                        setHeadFix={this.setHeadFix}
                                        content={aboutinfo}
                                    />
                                }
                            />
                            <Route
                                path="/login"
                                element={<Login setHeadFix={this.setHeadFix} />}
                            />
                            <Route
                                path="/dashboard/*"
                                element={
                                    <RequireAuth>
                                        <Dashboard
                                            setHeadFix={this.setHeadFix}
                                        />
                                    </RequireAuth>
                                }
                            />
                            <Route path="/item/:id" element={<Daily />} />
                            <Route
                                path="/404"
                                element={
                                    <Notfound setHeadFix={this.setHeadFix} />
                                }
                            />
                            <Route
                                path="*"
                                element={<Navigate to="/404" replace />}
                            />
                        </Routes>
                    </Suspense>
                </ErrorBoundary>
                {/* 始终挂载：进详情返回后面板与搜索状态原样保留，
                    显隐由组件内部按路由自行处理 */}
                <SearchSheet open={searchOpen} onClose={this.closeSearch} />
                <VideoPlayer />
                <Footer nopeAuth={this.setAuthenticatedLayout} sword={sword} />
            </section>
        );
    }
}

const ConnectedApp = connect(mapStateToProps)(App);

export default function AppWithLocation() {
    const location = useLocation();
    const prevPathname = useRef(location.pathname);

    useEffect(() => {
        if (prevPathname.current === location.pathname) return;
        prevPathname.current = location.pathname;
        // 进入帖子详情时复位滚动位置：
        // 首页滚到中途点进详情，浏览器会沿用原有滚动偏移，
        // 让详情页直接停在中途、footer 与正文叠在一起。
        if (location.pathname.startsWith("/item/")) {
            window.scrollTo(0, 0);
        }
    }, [location.pathname]);

    return <ConnectedApp location={location} />;
}
